<script setup lang='ts'>
import WisionLogo from '../WisionLogo.vue';
import Button from '../Button.vue';
import ScrollContainer from '../ScrollContainer.vue';
import { useAuth } from '#imports';
import { watch, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAppState } from '../../store/state';

const props = defineProps({
  onboarding: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const { signIn, status } = useAuth();
const route = useRoute();
const router = useRouter();
const state = useAppState();
const loading = ref<boolean>(false);

// Fetch configTag so we know if the tag is already ready
onMounted(async () => {
  const uuid = route.params.uuid as string;
  if (uuid && !state.configTag) {
    await state.fetchConfigTag(uuid);
  }
});

const getCallbackUrl = () => {
  const uuid = route.params.uuid;
  if (!props.onboarding || !uuid) return '/';
  // If tag is already configured, go directly to tag view
  if (state.configTag?.status === 'ready') return `/${uuid}`;
  return `/onboard/${uuid}/menu`;
};

const handleLogin = async () => {
  loading.value = true;
  await signIn('zitadel', { callbackUrl: getCallbackUrl(), redirect: true });
};

// Auto-redirect if already logged in (based on actual access, not token status)
watch(() => state.hasAccess, (hasAccess) => {
  if (hasAccess) {
    router.push(getCallbackUrl());
  }
}, { immediate: true });
</script>

<template>
  <div class="flex flex-col bg-onboard-background text-onboard-foreground font-sans">
    <ScrollContainer>
      <div class="m-auto flex justify-center max-w-2xl">
        <div class="flex flex-col items-center w-full px-10 text-onboard-shade2">
          <div class="flex flex-col mt-[14%] items-center">
            <WisionLogo :width="114" class="text-onboard-foreground"/>

            <span class="text-xl mt-6 uppercase">
              {{ $t('login.signin') }}
            </span>

            <div class="text-center mt-4 leading-none">
              <span class="text-sm uppercase">
                {{ $t('login.existingAccount') }}
              </span>
            </div>
          </div>

          <div class="w-full mt-6">
            <Button class="h-12" @click="handleLogin">
              <svg v-if="loading" class="w-6 h-6 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none"
                   viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span v-else>
                {{ $t('nfc.form.login') }}
              </span>
            </Button>
          </div>

          <span class="text-xl mb-1 mt-10 uppercase">
            {{ $t('nfc.terms.cookiesAndTerms') }}
          </span>
          <div class="text-center leading-none uppercase">
            <span class="text-sm">
              {{ $t('nfc.terms.acceptOnLogin') }}
              <a href="/doc/terms.pdf" class="underline" target="_blank" rel="noopener noreferrer">
                {{ $t('nfc.terms.pressHere') }}
              </a>
            </span>
          </div>
        </div>
      </div>
    </ScrollContainer>
    <span class="text-onboard-shade1 pt-5 pb-3 mt-auto text-center">WISION BUILD {{ state.build }}</span>
  </div>
</template>
